import { useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@artstorefronts/ui';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { SignIn, SignUp } from '@clerk/clerk-react';
import { getClerkAttributionUnsafeMetadata } from '@arthelper/shared/attribution';
import { decodeReferralHash } from '@arthelper/shared/referral-hash';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Gift invite tokens are `randomBytes(24).toString('hex')`. */
const GIFT_TOKEN_RE = /^[a-f0-9]{48}$/;

/**
 * A friend's invite on this page's URL (`?r=` / `?invite=` hash, `?gift=` token), shaped as
 * the town-square /signup sends it. Without it a referred signup here is counted as
 * unreferred — and held on the waitlist under a gated acquisition strategy.
 */
function friendInviteMetadata(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const hash = (params.get('r') ?? params.get('invite') ?? '').trim();
  const token = (params.get('gift') ?? '').trim().toLowerCase();
  return {
    ...(hash && decodeReferralHash(hash) !== null ? { referrerHash: hash } : {}),
    ...(GIFT_TOKEN_RE.test(token) ? { giftInviteToken: token } : {}),
  };
}

function useClerkAttributionUnsafeMetadata(open: boolean) {
  // Recompute when the modal opens so late URL captures are included; sync
  // read avoids the prior useEffect race on first OAuth click.
  return useMemo(() => {
    if (!open) return undefined;
    const meta = { ...getClerkAttributionUnsafeMetadata(), ...friendInviteMetadata() };
    return Object.keys(meta).length > 0 ? meta : undefined;
  }, [open]);
}

function SignInModal({ open, onOpenChange }: AuthModalProps) {
  const unsafeMetadata = useClerkAttributionUnsafeMetadata(open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <DialogTitle>Sign In</DialogTitle>
        <DialogDescription>Sign in to your ArtHelper account</DialogDescription>
      </VisuallyHidden>
      <DialogContent className="h-full w-full sm:h-auto sm:w-auto sm:max-w-[400px] flex flex-col justify-center p-0 sm:p-6">
        <SignIn unsafeMetadata={unsafeMetadata} />
      </DialogContent>
    </Dialog>
  );
}

function SignUpModal({ open, onOpenChange }: AuthModalProps) {
  const unsafeMetadata = useClerkAttributionUnsafeMetadata(open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <DialogTitle>Create Account</DialogTitle>
        <DialogDescription>Create your ArtHelper account</DialogDescription>
      </VisuallyHidden>
      <DialogContent className="h-full w-full sm:h-auto sm:w-auto sm:max-w-[400px] flex flex-col justify-center p-0 sm:p-6">
        <SignUp unsafeMetadata={unsafeMetadata} />
      </DialogContent>
    </Dialog>
  );
}

export { SignInModal, SignUpModal };
